import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, SlidersHorizontal, X } from "lucide-react";
import ProductGridSection from "../../components/customer/ProductGridSection";
import FilterSidebar from "../../components/customer/FilterSidebar";
import Dropdown, { Option } from "../../components/customer/Dropdown";
import { ProductItem } from "../../utils/Customer/CustomerTypes";
import CartDrawer, {
  CartItem,
} from "../../components/customer/ProductDetailComponent.tsx/CartDrawer";
import {
  useCreateCartMutation,
  useLazyDeleteCartQuery,
  useLazyGetCartQuery,
  useUpdateCartMutation,
} from "../../redux/actions/Customer/cartCustomer";
import { useAlert } from "../../components/customer/AlertContext";
import {
  useSearchProductsMutation,
  useGetSearchFiltersMutation,
} from "../../redux/actions/Customer/serviceCustomer";

import { ISearchProductsRequest } from "../../types/Request/CustomerRequest";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";

const sortOptions: Option[] = [
  { id: "1", label: "Relevance", value: "Relevance" },
  { id: "2", label: "Price: Low to High", value: "PriceLowToHigh" },
  { id: "3", label: "Price: High to Low", value: "PriceHighToLow" },
  { id: "4", label: "Newest", value: "Newest" },
];

const mapToProductItem = (item: any): ProductItem => ({
  productid: item.productId.toString(),
  ProductId: item.productId,
  sku: item.sku ?? "",
  category: "",
  subcategory: "",
  innerCategory: "",
  productname: item.name,
  brand: item.brandName ?? "",
  saleprice: (item.salePrice ?? 0).toString(),
  price: (item.price ?? 0).toString(),
  startDate: item.saleStartDate ?? "",
  endDate: item.saleEndDate ?? "",
  productimage: item.imageUrl,
  subimage: Array.isArray(item.images) ? item.images : [],
  quantity: item.stock ?? 0,
  productstatus: item.inStock,
  video: [],
  isSubscribe: false,
  description: item.shortDescription ?? "",
  keyFeatures: [],
  isReward: false,
  inStock: item.inStock,
  remainingSaleSeconds: item.remainingSaleSeconds ?? 0,
  marketingTexts: item.marketingTexts || item.MarketingTexts || [],
  rating: item.rating,
});



const CustomerProductListingScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showConfirm } = useAlert();

  const [cart, setCart] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState<Option | null>(sortOptions[0]);

  const [productsList, setProductsList] = useState<ProductItem[]>([]);
  const [relatedProductsList, setRelatedProductsList] = useState<ProductItem[]>([]);
  const isLoggedIn = useSelector(
    (state: RootState) => state.auth.customer.isLoggedIn
  );

  const source = location.state?.source;
  const stateData = location.state?.data;

  const categoryId = stateData?.categoryId ?? stateData?.categoryIds;
  const brandId = stateData?.brandId ?? stateData?.brandIds;
  const searchQuery = stateData?.query;
  const title = stateData?.title;
  const bannerImage = stateData?.mainImage || stateData?.imageUrl;

  const DEFAULT_PAGE_SIZE = 20;

  const normalizeIds = (val: any): any[] => {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  };

  const initialCategories = normalizeIds(categoryId);
  const initialBrands = normalizeIds(brandId);

  const [searchRequest, setSearchRequest] = useState<ISearchProductsRequest>({
    query: searchQuery || "",
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: "Relevance",
    brandIds: initialBrands,
    categoryIds: initialCategories,
    attributeValueIds: [],
    minPrice: stateData?.minPrice ?? 0,
    maxPrice: stateData?.maxPrice ?? 1000000,
    onlyInStock: false,
    onlyOnSale: stateData?.onlyOnSale ?? false,
    onlyNewArrivals: stateData?.onlyNewArrivals ?? false,
    onlyBestSeller: stateData?.onlyBestSeller ?? false,
    onlyPetlandRecommended: stateData?.onlyPetlandRecommended ?? false,
  });

  const [
    searchProducts,
    { data: searchResponse, isLoading: isProductsLoading },
  ] = useSearchProductsMutation();

  const [getSearchFilters, { data: filterResponse }] =
    useGetSearchFiltersMutation();
  const [createCart] = useCreateCartMutation();
  const [triggerGetCart] = useLazyGetCartQuery();
  const [updateCart] = useUpdateCartMutation();
  const [triggerDeleteCart] = useLazyDeleteCartQuery();

  // Calculate active filter count for the mobile button badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchRequest.categoryIds?.length) count += searchRequest.categoryIds.length;
    if (searchRequest.brandIds?.length) count += searchRequest.brandIds.length;
    if (searchRequest.attributeValueIds?.length) count += searchRequest.attributeValueIds.length;
    if (searchRequest.onlyOnSale) count++;
    if (searchRequest.onlyNewArrivals) count++;
    if (searchRequest.onlyBestSeller) count++;
    if (searchRequest.onlyPetlandRecommended) count++;
    if (searchRequest.onlyInStock) count++;
    if ((searchRequest.minRating ?? 0) >= 4) count++;
    return count;
  }, [searchRequest]);

  const syncCartWithServer = (serverItems: any[]) => {
    const mappedItems = serverItems.map((item) => ({
      id: item.cartItemId.toString(),
      productId: item.productId,
      title: item.productName,
      price: `AED ${item.unitSalePrice || item.unitPrice}`,
      image: item.imageUrl,
      quantity: item.quantity,
    }));
    setCart(mappedItems);
  };

  const fetchLatestCart = async () => {
    try {
      const res = await triggerGetCart().unwrap();
      if (res?.success && res?.data?.items) {
        syncCartWithServer(res.data.items);
      }
    } catch (error) {
      console.error("Cart retrieval error:", error);
    }
  };

  useEffect(() => {
    const categories = normalizeIds(categoryId);
    const brands = normalizeIds(brandId);

    setProductsList([]);
    setRelatedProductsList([]);
    setSelectedSort(sortOptions[0]);

    setSearchRequest({
      query: searchQuery || "",
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: "Relevance",
      brandIds: brands,
      categoryIds: categories,
      attributeValueIds: [],
      minPrice: stateData?.minPrice ?? 0,
      maxPrice: stateData?.maxPrice ?? 1000000,
      onlyInStock: false,
      onlyOnSale: stateData?.onlyOnSale ?? false,
      onlyNewArrivals: stateData?.onlyNewArrivals ?? false,
      onlyBestSeller: stateData?.onlyBestSeller ?? false,
      onlyPetlandRecommended: stateData?.onlyPetlandRecommended ?? false,
    });

    getSearchFilters({
      pageNumber: 1,
      pageSize: 20,
      brandIds: brands,
      categoryIds: categories,
      attributeValueIds: [],
      onlyInStock: false,
      onlyOnSale: false,
    });
  }, [location.key, categoryId, brandId, searchQuery]);

  useEffect(() => {
    searchProducts(searchRequest);
  }, [searchRequest, searchProducts]);

 useEffect(() => {
  if (!searchResponse?.items) return;

  const mapped = searchResponse.items.map(mapToProductItem);

  if (searchRequest.pageNumber === 1) {
    setProductsList(mapped);
  } else {
    setProductsList((prev) => [...prev, ...mapped]);
  }

  if (searchResponse.relatedProducts) {
    const mappedRelated = searchResponse.relatedProducts.map(mapToProductItem);
    setRelatedProductsList(mappedRelated);
  }
}, [searchResponse]);

  const observer = useRef<IntersectionObserver | null>(null);
  const totalPages = searchResponse?.totalPages ?? 1;
  const hasMore = searchRequest.pageNumber < totalPages;

  const lastProductElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isProductsLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setSearchRequest((prev) => ({
            ...prev,
            pageNumber: prev.pageNumber + 1,
          }));
        }
      });

      if (node) observer.current.observe(node);
    },
    [isProductsLoading, hasMore]
  );

  const handleFilterChange = (newRequest: ISearchProductsRequest) => {
    setProductsList([]);
    setSearchRequest({ ...newRequest, pageNumber: 1 });
  };

  const handleProductClick = (product: ProductItem) =>
    navigate(`/productpage/customerneproductpage/${product.ProductId}`, {
      state: { product },
    });

  const handleAddToCart = async (product: ProductItem) => {
    try {
      await createCart({
        productId: Number(product.ProductId),
        quantity: 1,
      }).unwrap();
      await fetchLatestCart();
      setIsOpen(true);
    } catch (errorMessage: any) {
      if (isLoggedIn) {
        showConfirm({
          title: "",
          message: errorMessage?.data?.Message || "Failed to add to cart.",
          type: "error",
          confirmText: "Ok",
        });
      }
    }
  };

  const handleCartIncrement = async (itemId: string) => {
    const currentItem = cart.find((item) => item.id === itemId);
    if (!currentItem) return;

    try {
      await updateCart({
        cartItemId: Number(itemId),
        quantity: currentItem.quantity + 1,
      }).unwrap();
      await fetchLatestCart();
    } catch (errorMessage: any) {
      showConfirm({
        title: "",
        message: errorMessage?.data?.Message || "Failed to update quantity.",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleCartDecrement = async (itemId: string) => {
    const currentItem = cart.find((item) => item.id === itemId);
    if (!currentItem) return;

    try {
      if (currentItem.quantity <= 1) {
        await handleCartRemove(itemId);
      } else {
        await updateCart({
          cartItemId: Number(itemId),
          quantity: currentItem.quantity - 1,
        }).unwrap();
        await fetchLatestCart();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCartRemove = async (itemId: string) => {
    try {
      await triggerDeleteCart(Number(itemId)).unwrap();
      await fetchLatestCart();
    } catch (error) {
      console.error(error);
    }
  };

  const isInitialLoading = isProductsLoading && searchRequest.pageNumber === 1;
  const isAllOutOfStock =
    productsList.length > 0 &&
    productsList.every((p) => p.quantity === 0 || p.inStock === false);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 text-left">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Desktop Sidebar */}
 <aside
  className="
    hidden
    lg:block
    lg:w-[240px]
    flex-shrink-0
    sticky
    top-6
    self-start
    max-h-[calc(100vh-3rem)]
    overflow-y-auto
    filter-sidebar-scroll
  "
>
  <FilterSidebar
    filters={filterResponse}
    searchRequest={searchRequest}
    onChange={handleFilterChange}
  />
</aside>

        {/* Mobile Filter Drawer Modal */}
        {isMobileFilterOpen && (
          <div className="fixed inset-0 z-50 lg:hidden flex">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
              onClick={() => setIsMobileFilterOpen(false)}
            />

            {/* Slide-over Content */}
            <div className="relative ml-auto w-[85%] max-w-sm h-full bg-white shadow-xl flex flex-col z-10 animate-in slide-in-from-right duration-200">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <span className="font-bold text-slate-800 text-base">Filters</span>
                <button
                  type="button"
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="p-1 rounded-full text-gray-500 hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <FilterSidebar
                  filters={filterResponse}
                  searchRequest={searchRequest}
                  onChange={(newReq) => {
                    handleFilterChange(newReq);
                  }}
                />
              </div>

              <div className="p-4 border-t border-gray-100 bg-white">
                <button
                  type="button"
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="w-full py-2.5 bg-[#2F5E2E] text-white rounded-xl font-semibold text-sm hover:bg-[#254b24] transition shadow-xs"
                >
                  View Products ({searchResponse?.totalCount ?? productsList.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 w-full relative">
          {(source === "BrandsSlider" || source === "PeekCarousel") &&
            bannerImage && (
              <div className="w-full mb-6 rounded-2xl overflow-hidden shadow-xs border border-gray-100">
                <img
                  src={bannerImage}
                  alt={title || "Listing Banner"}
                  className="w-full h-44 md:h-52 object-cover"
                />
              </div>
            )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">
              {title || (brandId ? "Explore Brand" : "Explore Products")}
            </h1>

            {/* Mobile Controls: Filter Trigger + Sort Dropdown */}
            <div className="flex items-center gap-2 justify-between sm:justify-end w-full sm:w-auto">
              {/* Filter Button for Mobile */}
              <button
                type="button"
                onClick={() => setIsMobileFilterOpen(true)}
                className="lg:hidden inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 text-xs font-semibold hover:bg-slate-100 active:scale-95 transition"
              >
                <SlidersHorizontal size={14} className="text-slate-600" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="w-4 h-4 text-[10px] font-bold rounded-full bg-[#2F5E2E] text-white flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-2 min-w-[170px] sm:min-w-[200px]">
                <span className="hidden sm:inline-block text-[11px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">
                  Sort By:
                </span>
                <Dropdown
                  options={sortOptions}
                  value={selectedSort}
                  onChange={(option) => {
                    setSelectedSort(option);
                    setProductsList([]);
                    setSearchRequest((prev) => ({
                      ...prev,
                      sortBy: option?.value,
                      pageNumber: 1,
                    }));
                  }}
                  className="w-full"
                  innerClassName="bg-slate-50 border-slate-200 !py-1.5 focus-within:ring-2 focus-within:ring-[#2F5E2E] text-xs font-semibold text-slate-700"
                  iconPosition="none"
                />
              </div>
            </div>
          </div>

          {isInitialLoading ? (
            <div className="w-full py-24 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-[#2F5E2E]" />
            </div>
          ) : (
            <>
              {productsList.length === 0 && (
                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-center">
                  <span className="mb-1 block text-2xl">🐾</span>
                  <h3 className="font-semibold">No products found</h3>
                  <p className="mt-2 text-xs text-slate-500">
                    We couldn't find any products matching your search. Try
                    changing your filters.
                  </p>
                </div>
              )}

              {productsList.length > 0 && (
                <ProductGridSection
                  products={productsList}
                  cartItems={cart}
                  handleProductPage={handleProductClick}
                  handleAddToCart={handleAddToCart}
                  handleIncrementToCart={(p) => {
                    const item = cart.find((c) => c.productId === p.ProductId);
                    if (item) handleCartIncrement(item.id);
                  }}
                  handleDecrementFromCart={(p) => {
                    const item = cart.find((c) => c.productId === p.ProductId);
                    if (item) handleCartDecrement(item.id);
                  }}
                />
              )}

              {(productsList.length === 0 || isAllOutOfStock) &&
                relatedProductsList.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-gray-200">
                    <div className="mb-4">
                      <h2 className="text-lg font-bold text-slate-800">
                        You Might Also Like
                      </h2>
                      <p className="text-xs text-slate-500">
                        Check out these related items that are currently in
                        stock.
                      </p>
                    </div>

                    <ProductGridSection
                      products={relatedProductsList}
                      displayMode="grid"
                      cartItems={cart}
                      handleProductPage={handleProductClick}
                      handleAddToCart={handleAddToCart}
                      handleIncrementToCart={(p) => {
                        const item = cart.find(
                          (c) => c.productId === p.ProductId
                        );
                        if (item) handleCartIncrement(item.id);
                      }}
                      handleDecrementFromCart={(p) => {
                        const item = cart.find(
                          (c) => c.productId === p.ProductId
                        );
                        if (item) handleCartDecrement(item.id);
                      }}
                    />
                  </div>
                )}

              <div
                ref={lastProductElementRef}
                className="w-full py-6 flex items-center justify-center h-12"
              >
                {isProductsLoading && searchRequest.pageNumber > 1 && (
                  <Loader2 size={24} className="animate-spin text-[#2F5E2E]" />
                )}
              </div>
            </>
          )}
        </main>

        <CartDrawer
          cart={cart}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onIncrement={handleCartIncrement}
          onDecrement={handleCartDecrement}
          onRemove={handleCartRemove}
          onAddToCart={handleAddToCart}
          handleCheckoutClick={() => navigate("/cartpage/customercartpage")}
        />
      </div>
    </div>
  );
};

export default CustomerProductListingScreen;